/**
 * 도구 시험 화면 (기반 공사 5단계, 시험용 서버에만. 공통/작업규칙 9절 2026-11-04)
 *
 * 대표님이 5단계 도구를 직접 눌러 보는 곳이다. 소장님 화면이 아니라 디자인과 문구는 시험용이다.
 * 번호 검사, 주소 검색, 사진 올리기와 돌리기 자르기, 직인, 경고창, 공휴일과 체류 만료 표시, 계산 엔진, 서류 뽑기.
 */
import { useEffect, useRef, useState, type CSSProperties, type MouseEvent } from 'react';
import {
  ageRule, bizNoMessage, calcDay, checkBizNo, checkResidentNo, payoutTax, residentNoMessage, todayKst, visaBadge,
  type Standards, type TaxMethod
} from '@ilsoni/shared';
import { api } from '../api.ts';
import { useDialog } from '../dialog.tsx';
import { ACCEPT_PHOTO_PDF, readPicked, type PickedFile } from '../photo.ts';
import { Button, Field, Link, Message, Page } from '../ui.tsx';

const section: CSSProperties = { borderTop: '1px solid #ccc', padding: '16px 0 8px', marginTop: 16 };
const chip: CSSProperties = { display: 'inline-block', fontSize: 14, padding: '0 8px', marginRight: 6, borderRadius: 10, background: '#e9e9ec' };

export function Tools(props: { go: (p: string) => void }) {
  const dialog = useDialog();
  return (
    <Page title="도구 시험 (시험용 서버)">
      <p><Link to="/" go={props.go}>처음 화면</Link></p>
      <NumberCheck dialog={dialog} />
      <AddressSearch />
      <PhotoUpload />
      <SealUpload />
      <DialogTry dialog={dialog} />
      <Days />
      <MoneyTry />
      <Documents />
      {dialog.element}
    </Page>
  );
}

type Dialog = ReturnType<typeof useDialog>;

function NumberCheck({ dialog }: { dialog: Dialog }) {
  const [rrn, setRrn] = useState('');
  const [biz, setBiz] = useState('');
  const [note, setNote] = useState<string | null>(null);
  async function saveRrn() {
    const r = checkResidentNo(rrn);
    if (!r.ok) {
      await dialog.block(residentNoMessage(r) ?? '번호를 다시 확인해 주세요.');
      return;
    }
    const age = ageRule(r.birth);
    if (age === '막음') {
      await dialog.block('만 15세 미만은 등록할 수 없어요.');
      return;
    }
    setNote(`저장할 수 있어요: ${r.birth}, ${r.sex}, ${r.foreign ? '외국인' : '내국인'}${age === '경고' ? ', 만 18세 미만(배차 전에 경고)' : ''}`);
  }
  async function saveBiz() {
    const r = checkBizNo(biz);
    if (!r.ok) await dialog.block(bizNoMessage(r) ?? '사업자번호를 다시 확인해 주세요.');
    else setNote(`저장할 수 있어요: ${r.digits}`);
  }
  return (
    <section style={section}>
      <h2>번호 검사</h2>
      <Field label="주민등록번호(외국인등록번호)" value={rrn} onChange={setRrn} />
      <Button onClick={() => void saveRrn()}>저장해 보기</Button>
      <Field label="사업자등록번호" value={biz} onChange={setBiz} />
      <Button onClick={() => void saveBiz()}>저장해 보기</Button>
      <Message text={note} kind="info" />
    </section>
  );
}

interface Hit { road: string; jibun: string; zip: string; building: string }

function AddressSearch() {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<Hit[] | null>(null);
  const [picked, setPicked] = useState<Hit | null>(null);
  const [error, setError] = useState<string | null>(null);
  async function search() {
    setError(null);
    const r = await api<{ hits: Hit[] }>('GET', `/api/address?q=${encodeURIComponent(q)}`);
    if (r.ok) setHits(r.hits);
    else { setHits(null); setError(r.message); }
  }
  return (
    <section style={section}>
      <h2>주소 검색</h2>
      <Field label="주소" value={q} onChange={setQ} />
      <Button onClick={() => void search()}>찾기</Button>
      <Message text={error} />
      {hits && hits.length === 0 && <p>찾은 주소가 없어요. 적은 그대로 저장할 수 있어요.</p>}
      {hits?.map((h) => (
        <button
          key={h.road + h.jibun}
          type="button"
          onClick={() => setPicked(h)}
          style={{ display: 'block', width: '100%', textAlign: 'left', fontSize: 18, padding: 8, margin: '4px 0', background: picked === h ? '#fde7da' : '#fff', border: '1px solid #ccc', borderRadius: 6 }}
        >
          <div><span style={{ ...chip, background: '#1A202B', color: '#fff' }}>도로명</span>{h.road}</div>
          <div><span style={chip}>지번</span>{h.jibun}</div>
        </button>
      ))}
      {picked && <p>고른 주소: {picked.road} ({picked.zip})</p>}
    </section>
  );
}

interface Crop { x: number; y: number; w: number; h: number }

function PhotoUpload() {
  const [file, setFile] = useState<PickedFile | null>(null);
  const [rotate, setRotate] = useState(0);
  const [crop, setCrop] = useState<Crop | null>(null);
  const [shown, setShown] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [camera, setCamera] = useState(false);
  const drag = useRef<{ x: number; y: number } | null>(null);
  const [box, setBox] = useState<Crop | null>(null);

  useEffect(() => {
    if (!file || file.kind !== '사진') return;
    void (async () => {
      const r = await api<{ data: string; bytes_before: number; bytes_stored: number }>('POST', '/api/tools/photo', {
        data: file.data_url, adjust: { rotate, ...(crop ? { crop } : {}) }
      });
      if (r.ok) {
        setShown(`data:image/jpeg;base64,${r.data}`);
        setInfo(`원본 ${Math.round(r.bytes_before / 1024)}KB, 저장 ${Math.round(r.bytes_stored / 1024)}KB${file.converted ? ', 아이폰 사진을 바꿈' : ''}`);
        setError(null);
      } else setError(r.message);
    })();
  }, [file, rotate, crop]);

  async function pick(f: File | undefined) {
    if (!f) return;
    setError(null); setShown(null); setRotate(0); setCrop(null);
    try {
      const p = await readPicked(f);
      if (p.kind === 'PDF') {
        const r = await api<{ bytes: number }>('POST', '/api/tools/pdf', { data: p.data_url });
        setInfo(r.ok ? `PDF를 받았어요(${Math.round(r.bytes / 1024)}KB).` : null);
        if (!r.ok) setError(r.message);
        setFile(null);
        return;
      }
      setFile(p);
    } catch {
      setError('사진을 읽지 못했어요.');
    }
  }

  function point(e: MouseEvent<HTMLDivElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)), y: Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height)) };
  }

  return (
    <section style={section}>
      <h2>사진 올리기</h2>
      <label style={{ display: 'block', marginBottom: 8 }}>
        <input type="checkbox" checked={camera} onChange={(e) => setCamera(e.target.checked)} /> 휴대폰에서 카메라 바로 열기
      </label>
      <input
        type="file"
        accept={ACCEPT_PHOTO_PDF}
        {...(camera ? { capture: 'environment' as const } : {})}
        onChange={(e) => void pick(e.target.files?.[0])}
        style={{ fontSize: 18 }}
      />
      <Message text={error} />
      <Message text={info} kind="info" />
      {shown && (
        <>
          <p>
            <Button onClick={() => { setCrop(null); setRotate((rotate + 270) % 360); }}>왼쪽으로 돌리기</Button>
            <Button onClick={() => { setCrop(null); setRotate((rotate + 90) % 360); }}>오른쪽으로 돌리기</Button>
            <Button onClick={() => { setRotate(0); setCrop(null); setBox(null); }}>원래대로</Button>
          </p>
          <p style={{ fontSize: 16 }}>사진 위를 끌어서 자를 곳을 고른 뒤 "자르기"를 누르세요.</p>
          <div
            style={{ position: 'relative', display: 'inline-block', cursor: 'crosshair', userSelect: 'none' }}
            onMouseDown={(e) => { drag.current = point(e); setBox(null); }}
            onMouseMove={(e) => {
              if (!drag.current) return;
              const p = point(e);
              const s = drag.current;
              setBox({ x: Math.min(s.x, p.x), y: Math.min(s.y, p.y), w: Math.abs(p.x - s.x), h: Math.abs(p.y - s.y) });
            }}
            onMouseUp={() => { drag.current = null; }}
          >
            <img src={shown} alt="올린 사진" style={{ maxWidth: '100%', maxHeight: 420, display: 'block' }} draggable={false} />
            {box && (
              <div style={{ position: 'absolute', left: `${box.x * 100}%`, top: `${box.y * 100}%`, width: `${box.w * 100}%`, height: `${box.h * 100}%`, border: '2px dashed #EB6515' }} />
            )}
          </div>
          {box && !crop && box.w > 0.05 && box.h > 0.05 && (
            <p><Button primary onClick={() => { setCrop(box); setBox(null); }}>자르기</Button></p>
          )}
        </>
      )}
    </section>
  );
}

function SealUpload() {
  const [png, setPng] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  async function pick(f: File | undefined) {
    if (!f) return;
    const p = await readPicked(f);
    const r = await api<{ data: string }>('POST', '/api/tools/seal', { data: p.data_url });
    if (r.ok) { setPng(`data:image/png;base64,${r.data}`); setError(null); } else setError(r.message);
  }
  return (
    <section style={section}>
      <h2>직인 (흰 배경 지우기)</h2>
      <input type="file" accept="image/*,.heic,.heif" onChange={(e) => void pick(e.target.files?.[0])} style={{ fontSize: 18 }} />
      <Message text={error} />
      {png && (
        <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
          <img src={png} alt="직인" style={{ maxHeight: 140, background: '#fff', padding: 8, border: '1px solid #ccc' }} />
          <img src={png} alt="색 바탕 위 직인" style={{ maxHeight: 140, background: '#cfe3ff', padding: 8 }} />
        </div>
      )}
    </section>
  );
}

function DialogTry({ dialog }: { dialog: Dialog }) {
  const [result, setResult] = useState<string | null>(null);
  const [value, setValue] = useState('');
  return (
    <section style={section}>
      <h2>경고창</h2>
      <Button onClick={() => void dialog.block('같은 주민등록번호의 인력이 이미 있어요.')}>막는 창</Button>
      <Button onClick={() => void dialog.ask({ problem: '유효하지 않은 체류기간이에요.', result: '제대로 서류가 만들어지지 않아요.' })
        .then((a) => setResult(a === 'go' ? '그래도 하기를 골랐어요.' : '고치러 가기를 골랐어요.'))}>묻는 창</Button>
      <Button onClick={() => void dialog.need({
        problem: '직인이 아직 없어요. 지금 넣어 주세요.',
        body: <input value={value} onChange={(e) => setValue(e.target.value)} placeholder="(시험) 아무 글자" style={{ fontSize: 20, width: '100%' }} />,
        later: '그만두기'
      }).then((a) => setResult(a === 'go' ? '받고 이어서 했어요.' : '그만뒀어요.'))}>받는 창</Button>
      <Message text={result} kind="info" />
    </section>
  );
}

function Days() {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [list, setList] = useState<Array<{ day: string; name: string }> | null>(null);
  const [visa, setVisa] = useState('');
  async function load() {
    const r = await api<{ holidays: Array<{ day: string; name: string }> }>('GET', `/api/tools/holidays?year=${year}`);
    setList(r.ok ? r.holidays : []);
  }
  const badge = /^\d{4}-\d{2}-\d{2}$/.test(visa) ? visaBadge(visa, todayKst()) : null;
  return (
    <section style={section}>
      <h2>공휴일과 체류 만료 표시</h2>
      <Field label="연도" value={year} onChange={setYear} />
      <Button onClick={() => void load()}>공휴일 보기</Button>
      {list && (list.length === 0 ? <p>그해 공휴일이 아직 없어요.</p> : (
        <ul style={{ fontSize: 16 }}>{list.map((h) => <li key={h.day}>{h.day} {h.name}</li>)}</ul>
      ))}
      <Field label="체류기간 만료일(YYYY-MM-DD)" value={visa} onChange={setVisa} />
      {badge && <p><span style={{ ...chip, fontSize: 18, background: badge.pin_top ? '#EB6515' : '#ccc', color: badge.pin_top ? '#fff' : '#111' }}>{badge.text}</span>{badge.pin_top ? ' 목록 맨 위' : ' 원래 자리'}</p>}
      {/^\d{4}-\d{2}-\d{2}$/.test(visa) && !badge && <p>표시 없음(30일보다 많이 남음)</p>}
    </section>
  );
}

function MoneyTry() {
  const [std, setStd] = useState<Standards | null>(null);
  const [price, setPrice] = useState('150000');
  const [gongsu, setGongsu] = useState('1');
  const [method, setMethod] = useState<TaxMethod>('일반 소득세');
  const [officeTax, setOfficeTax] = useState(false);
  useEffect(() => {
    void api<{ standards: Standards }>('GET', `/api/tools/standards?day=${todayKst()}`).then((r) => { if (r.ok) setStd(r.standards); });
  }, []);
  let out: string[] = [];
  try {
    const d = calcDay({ unit_price: Number(price), gongsu: Number(gongsu), fee: { kind: 'rate', employer_pct: 10, worker_pct: 1 } });
    const tax = std && officeTax ? payoutTax([d.taxable], method, std) : { income_tax: 0, local_tax: 0 };
    out = [
      `소개요금 ${d.fee_total.toLocaleString()}원`,
      `세금 ${tax.income_tax.toLocaleString()}원 + ${tax.local_tax.toLocaleString()}원`,
      `인력이 받는 돈 ${(d.pay_before_tax - tax.income_tax - tax.local_tax).toLocaleString()}원`,
      `거래처 청구 ${d.bill_amount.toLocaleString()}원`
    ];
  } catch (e) {
    out = [e instanceof Error ? e.message : '계산할 수 없어요.'];
  }
  return (
    <section style={section}>
      <h2>계산 엔진 (화면과 서버가 같은 계산)</h2>
      <Field label="단가(원)" value={price} onChange={setPrice} />
      <Field label="공수" value={gongsu} onChange={setGongsu} />
      <label style={{ display: 'block' }}><input type="checkbox" checked={officeTax} onChange={(e) => setOfficeTax(e.target.checked)} /> 사무소가 세금을 뗌(기본은 거래처)</label>
      {officeTax && (
        <p>
          {(['일반 소득세', '3.3%'] as const).map((m) => (
            <label key={m} style={{ marginRight: 16 }}><input type="radio" checked={method === m} onChange={() => setMethod(m)} /> {m}</label>
          ))}
        </p>
      )}
      <ul>{out.map((t) => <li key={t}>{t}</li>)}</ul>
    </section>
  );
}

const FILLS = [['job', '직종'], ['price', '임금(단가)'], ['gongsu', '공수'], ['time', '작업 시간'], ['manager', '현장 담당']] as const;

function Documents() {
  const [kind, setKind] = useState<'작업확인서' | '종사자명부'>('작업확인서');
  const [fill, setFill] = useState<Set<string>>(new Set());
  const url = (format: 'pdf' | 'xlsx', print = false) =>
    `/api/tools/document?kind=${encodeURIComponent(kind)}&format=${format}${print ? '&print=1' : ''}&fill=${[...fill].join(',')}`;
  return (
    <section style={section}>
      <h2>서류 뽑기 (예시 자료)</h2>
      <p>
        {(['작업확인서', '종사자명부'] as const).map((k) => (
          <label key={k} style={{ marginRight: 16 }}><input type="radio" checked={kind === k} onChange={() => setKind(k)} /> {k}</label>
        ))}
      </p>
      {kind === '작업확인서' && (
        <p>
          미리 채울 칸:{' '}
          {FILLS.map(([key, label]) => (
            <label key={key} style={{ marginRight: 12 }}>
              <input
                type="checkbox"
                checked={fill.has(key)}
                onChange={(e) => {
                  const next = new Set(fill);
                  if (e.target.checked) next.add(key); else next.delete(key);
                  setFill(next);
                }}
              /> {label}
            </label>
          ))}
        </p>
      )}
      <p>
        <a href={url('pdf', true)} target="_blank" rel="noreferrer" style={{ marginRight: 16 }}>인쇄하기(PDF 열기)</a>
        <a href={url('pdf')} style={{ marginRight: 16 }}>PDF로 받기</a>
        <a href={url('xlsx')}>엑셀로 받기</a>
      </p>
    </section>
  );
}
